import * as React from 'react';
import { Link, ChevronDown, MoreHorizontal } from 'lucide-react';

export interface BreadcrumbItem {
  /**
   * The label to display for this breadcrumb
   */
  label: string;
  /**
   * Optional href for the breadcrumb link
   */
  href?: string;
  /**
   * Optional click handler
   */
  onClick?: () => void;
  /**
   * Whether this breadcrumb has a dropdown menu
   */
  hasDropdown?: boolean;
}

export interface BreadcrumbsProps {
  /**
   * Array of breadcrumb items
   */
  items: BreadcrumbItem[];
  /**
   * Callback when copy button is clicked
   */
  onCopy?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

/**
 * Breadcrumbs component - Arbor Design System
 *
 * Navigation component showing the current page's location in the site hierarchy.
 * Automatically truncates to show first and last breadcrumb with ellipsis when more than 6 items.
 */
export const Breadcrumbs = React.forwardRef<HTMLDivElement, BreadcrumbsProps>(
  (
    {
      items,
      onCopy,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
    const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);
    const [showCopyTooltip, setShowCopyTooltip] = React.useState(false);
    const [ellipsisFocused, setEllipsisFocused] = React.useState(false);
    const [ellipsisHovered, setEllipsisHovered] = React.useState(false);

    // Truncate breadcrumbs if more than 6 items
    const displayItems = React.useMemo(() => {
      if (items.length > 6) {
        // Show first item, ellipsis, and last item
        return [items[0], { label: '...', isEllipsis: true }, items[items.length - 1]];
      }
      return items;
    }, [items]);

    const handleCopy = () => {
      onCopy?.();
      // Copy the breadcrumb trail to clipboard
      const trail = items.map(item => item.label).join(' / ');
      navigator.clipboard.writeText(trail);
    };

    const containerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      ...style,
    };

    const breadcrumbItemStyles = (index: number, isActive: boolean, isEllipsis?: boolean): React.CSSProperties => {
      const isFocused = focusedIndex === index;

      if (isEllipsis) {
        return {
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          height: '24px',
          overflow: 'hidden',
        };
      }

      return {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        height: '24px',
        overflow: 'hidden',
        backgroundColor: isFocused ? 'rgba(255, 255, 255, 0.01)' : 'transparent',
        borderRadius: '99px',
        boxShadow: isFocused ? '0px 0px 0px 3px #3cad51' : 'none',
        padding: isFocused ? '0 6px' : '0 2px',
        margin: isFocused ? '0' : '0 4px',
        cursor: isActive ? 'default' : 'pointer',
        textDecoration: 'none',
      };
    };

    const linkStyles = (isActive: boolean, isHovered: boolean): React.CSSProperties => ({
      fontFamily: isActive
        ? "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        : "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: '13px',
      fontWeight: isActive ? 600 : 400,
      color: isActive
        ? isHovered
          ? '#0e8a0e'
          : '#2f2f2f'
        : isHovered
        ? '#0e8a0e'
        : '#595959',
      lineHeight: '1.5',
      whiteSpace: 'nowrap',
    });

    const dividerStyles: React.CSSProperties = {
      fontFamily: "'PT Sans', sans-serif",
      fontSize: '14px',
      color: '#595959',
      lineHeight: 'normal',
      whiteSpace: 'nowrap',
    };

    const ellipsisButtonStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '99px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      backgroundColor: ellipsisFocused ? 'rgba(255, 255, 255, 0.01)' : ellipsisHovered ? '#efefef' : 'transparent',
      boxShadow: ellipsisFocused ? '0px 0px 0px 3px #3cad51' : 'none',
    };

    const copyButtonStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '99px',
      border: 'none',
      background: showCopyTooltip ? '#efefef' : 'transparent',
      cursor: 'pointer',
      position: 'relative',
    };

    const tooltipStyles: React.CSSProperties = {
      position: 'absolute',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: '8px',
      backgroundColor: '#2f2f2f',
      color: 'white',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '13px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontWeight: 400,
      lineHeight: '1.5',
      whiteSpace: 'nowrap',
      boxShadow: '0px 4px 12px rgba(32, 32, 32, 0.08)',
      zIndex: 1000,
    };

    return (
      <div
        ref={ref}
        className={className}
        style={containerStyles}
        data-testid={dataTestId}
      >
        {displayItems.map((item, index) => {
          const isActive = index === displayItems.length - 1;
          const isEllipsis = 'isEllipsis' in item && item.isEllipsis;

          if (isEllipsis) {
            return (
              <React.Fragment key={`ellipsis-${index}`}>
                <button
                  style={ellipsisButtonStyles}
                  onFocus={() => setEllipsisFocused(true)}
                  onBlur={() => setEllipsisFocused(false)}
                  onMouseEnter={() => setEllipsisHovered(true)}
                  onMouseLeave={() => setEllipsisHovered(false)}
                  aria-label="More breadcrumbs"
                >
                  <MoreHorizontal
                    size={12}
                    color="#2f2f2f"
                    strokeWidth={2}
                    style={{ display: 'block', flexShrink: 0 }}
                  />
                </button>
                <span style={dividerStyles}> /</span>
              </React.Fragment>
            );
          }

          const breadcrumbItem = item as BreadcrumbItem;

          if (isActive) {
            // Active breadcrumb (last item) - not clickable
            return (
              <React.Fragment key={index}>
                <div
                  style={breadcrumbItemStyles(index, true)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={() => setFocusedIndex(null)}
                  tabIndex={0}
                >
                  <span style={linkStyles(true, hoveredIndex === index)}>
                    {breadcrumbItem.label}
                  </span>
                </div>
                {/* Copy button - appears immediately after active breadcrumb */}
                {onCopy && (
                  <button
                    style={copyButtonStyles}
                    onClick={handleCopy}
                    onMouseEnter={() => setShowCopyTooltip(true)}
                    onMouseLeave={() => setShowCopyTooltip(false)}
                    aria-label="Copy breadcrumb trail"
                  >
                    <Link
                      size={12}
                      color="#2f2f2f"
                      strokeWidth={2}
                      style={{ display: 'block', flexShrink: 0 }}
                    />
                    {showCopyTooltip && (
                      <div style={tooltipStyles}>Copy breadcrumb trail</div>
                    )}
                  </button>
                )}
              </React.Fragment>
            );
          }

          // Regular breadcrumb link
          const Element = breadcrumbItem.href ? 'a' : 'button';
          return (
            <React.Fragment key={index}>
              <Element
                {...(breadcrumbItem.href ? { href: breadcrumbItem.href } : {})}
                style={{
                  ...breadcrumbItemStyles(index, false),
                  border: 'none',
                  background: 'transparent',
                }}
                onClick={(e) => {
                  if (!breadcrumbItem.href && breadcrumbItem.onClick) {
                    e.preventDefault();
                    breadcrumbItem.onClick();
                  }
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onFocus={() => setFocusedIndex(index)}
                onBlur={() => setFocusedIndex(null)}
              >
                <span style={linkStyles(false, hoveredIndex === index)}>
                  {breadcrumbItem.label}
                </span>
                {breadcrumbItem.hasDropdown && (
                  <ChevronDown
                    size={12}
                    color={hoveredIndex === index ? '#0e8a0e' : '#595959'}
                    strokeWidth={2}
                    style={{ marginLeft: '2px', display: 'inline-block', flexShrink: 0 }}
                  />
                )}
              </Element>
              <span style={dividerStyles}> /</span>
            </React.Fragment>
          );
        })}
      </div>
    );
  }
);

Breadcrumbs.displayName = 'Breadcrumbs';
